import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Mail, ArrowRight } from 'lucide-react';
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};
export function WhiteWhisperer() {
  return <div className="min-h-screen bg-[#0a0a0a] text-gray-100 font-sans selection:bg-[#2DD4BF] selection:text-[#0a0a0a] overflow-x-hidden" data-id="element-2">
      {/* HERO SECTION */}
      <section className="relative min-h-screen flex flex-col lg:flex-row" data-id="element-3">
        {/* Text Content */}
        <div className="w-full lg:w-[45%] flex flex-col justify-center px-6 py-12 lg:p-20 z-10 order-2 lg:order-1" data-id="element-4">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="space-y-8" data-id="element-5">
            <motion.h1 variants={fadeIn} className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tight text-white" data-id="element-6">
              the white
              <br data-id="element-7" />
              <span className="italic text-[#2DD4BF]" data-id="element-8">whisperer</span>
            </motion.h1>

            <motion.div variants={fadeIn} className="space-y-2" data-id="element-9">
              <p className="text-xl md:text-2xl font-light text-gray-300" data-id="element-10">
                Sundays 7:30-9pm EST
              </p>
              <p className="text-lg text-gray-500" data-id="element-11">
                April 12 · May 17 · June 14, 2026
              </p>
              <p className="text-lg text-gray-400" data-id="element-12">with Gorge Michael</p>
            </motion.div>

            <motion.div variants={fadeIn} className="pt-8" data-id="element-13">
              <a href="mailto:gorgemichael@proton.me" className="inline-flex items-center gap-3 text-[#2DD4BF] hover:text-white transition-colors duration-300 text-lg group" data-id="element-14">
                <Mail className="w-5 h-5" data-id="element-15" />
                <span data-id="element-16">email gorgemichael@proton.me for link</span>
                <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" data-id="element-17" />
              </a>
            </motion.div>
          </motion.div>
        </div>

        {/* Hero Image */}
        <div className="w-full lg:w-[55%] h-[50vh] lg:h-screen relative order-1 lg:order-2" data-id="element-18">
          <div className="absolute inset-0 bg-gradient-to-b lg:bg-gradient-to-r from-[#0a0a0a] via-transparent to-transparent z-10 opacity-80 lg:opacity-100" data-id="element-19" />
          <img src="https://cdn.magicpatterns.com/uploads/fx2PXfar8zRWuhpzN6L64p/GORGE_MICHAEL0935WEB.jpg" alt="Gorge Michael reclining" className="w-full h-full object-cover object-right" data-id="element-20" />
        </div>
      </section>

      {/* ABOUT SECTION */}
      <section className="py-24 px-6 md:px-12 lg:px-24 max-w-7xl mx-auto" data-id="element-21">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="grid md:grid-cols-12 gap-12 items-start" data-id="element-22">
          <div className="md:col-span-5" data-id="element-23">
            <motion.h2 variants={fadeIn} className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight" data-id="element-24">
              Are you complicit in{' '}
              <span className="text-[#2DD4BF] italic" data-id="element-25">white supremacy?</span>
            </motion.h2>
          </div>

          <div className="md:col-span-7 space-y-12" data-id="element-26">
            <motion.ul variants={staggerContainer} className="space-y-8" data-id="element-27">
              {['Are you part of the machine that "loses" women in Ontario (MMIWG2S)?', 'Are you part of the machine that encourages police shooting and incarceration of Black and Indigenous boys in so-called Canada?', 'Are you part of the machine genociding Gaza for real estate, and Sudan and Congo for extraction?'].map((item, index) => <motion.li key={index} variants={fadeIn} className="flex gap-6 items-start group" data-id="element-28">
                  <span className="w-3 h-3 mt-2.5 rounded-full bg-[#2DD4BF] flex-shrink-0 shadow-[0_0_10px_rgba(45,212,191,0.5)]" data-id="element-29" />
                  <p className="text-xl md:text-2xl font-light leading-relaxed text-gray-200" data-id="element-30">
                    {item}
                  </p>
                </motion.li>)}
            </motion.ul>

            <motion.p variants={fadeIn} className="text-2xl md:text-3xl font-serif italic text-[#2DD4BF] pt-8 border-t border-gray-800" data-id="element-31">
              If you're nodding then white whispering might be for you.
            </motion.p>
          </div>
        </motion.div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 bg-[#111] relative overflow-hidden" data-id="element-32">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#2DD4BF] to-transparent opacity-20" data-id="element-33" />

        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center" data-id="element-34">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} data-id="element-35">
            <motion.h2 variants={fadeIn} className="font-serif text-3xl md:text-4xl mb-12 text-gray-400 tracking-wide uppercase text-sm font-sans font-bold" data-id="element-36">
              How It Works
            </motion.h2>

            <motion.p variants={fadeIn} className="text-xl md:text-2xl leading-relaxed font-light text-gray-300" data-id="element-37">
              This work is respectfully based in Indigenous ways of knowing and
              being. We'll use Indigenous protocol and work in a circle with
              each member invited to share in sequence about the applications of
              given idea. There is no need to have done any prior work with
              Indigeneity or whiteness. We are readying ourselves for the work
              of gently guiding ourselves and others toward repair and
              reconciliation.
            </motion.p>

            <motion.div variants={fadeIn} className="mt-12" data-id="element-38">
              <img src="https://cdn.magicpatterns.com/uploads/3xVruDz1V3ugc7WHM9rE31/Screenshot_2026-04-01_at_6.30.42_PM.png" alt="The White Whisperer group session on video call" className="w-full rounded-sm border border-gray-800" data-id="element-39" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* QUOTE SECTION */}
      <section className="py-32 px-6 md:px-12 max-w-6xl mx-auto" data-id="element-40">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={staggerContainer} className="text-center space-y-12" data-id="element-41">
          <motion.blockquote variants={fadeIn} className="font-serif text-4xl md:text-6xl lg:text-7xl leading-tight text-white relative inline-block" data-id="element-42">
            <span className="absolute -top-8 -left-8 md:-top-12 md:-left-12 text-8xl text-[#2DD4BF] opacity-20 font-serif" data-id="element-43">
              "
            </span>
            Your detachedness doesn't exempt you from being here now
            <span className="absolute -bottom-12 -right-8 md:-bottom-16 md:-right-12 text-8xl text-[#2DD4BF] opacity-20 font-serif" data-id="element-44">
              "
            </span>
          </motion.blockquote>

          <motion.div variants={fadeIn} className="flex flex-col items-center gap-2" data-id="element-45">
            <cite className="text-lg md:text-xl text-[#2DD4BF] not-italic font-medium" data-id="element-46">
              — @okayshalom
            </cite>
            <span className="text-gray-500" data-id="element-47">speaking to white followers</span>
          </motion.div>

          <motion.div variants={fadeIn} className="max-w-2xl mx-auto mt-16 p-8 border border-gray-800 bg-[#0f0f0f] rounded-sm relative" data-id="element-48">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#0a0a0a] px-4 text-gray-500 text-sm uppercase tracking-widest" data-id="element-49">
              A Note from Gorge
            </div>
            <p className="text-lg text-gray-300 italic leading-relaxed" data-id="element-50">
              "Shhhh... in the here and now, the plan is slavery and we white
              guys are complicit. Fortunately we are immensely privileged which
              means we can effect change. We've got this... xx Gorge the White
              Whisperer"
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* FOOTER */}
      <footer className="py-20 px-6 border-t border-gray-900 bg-[#050505]" data-id="element-51">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={staggerContainer} className="max-w-4xl mx-auto text-center space-y-8" data-id="element-52">
          <motion.div variants={fadeIn} data-id="element-53">
            <h3 className="font-serif text-3xl mb-2" data-id="element-54">Join the Circle</h3>
            <p className="text-gray-400" data-id="element-55">Sundays 7:30-9pm EST</p>
            <p className="text-gray-500 text-sm mt-1" data-id="element-56">
              April 12 · May 17 · June 14
            </p>
          </motion.div>

          <motion.div variants={fadeIn} data-id="element-57">
            <a href="mailto:gorgemichael@proton.me" className="inline-block bg-[#2DD4BF] text-black font-semibold px-8 py-4 text-lg md:text-xl rounded-full hover:bg-white transition-all duration-300 transform hover:scale-105 shadow-[0_0_20px_rgba(45,212,191,0.3)] hover:shadow-[0_0_30px_rgba(255,255,255,0.4)]" data-id="element-58">
              gorgemichael@proton.me
            </a>
          </motion.div>

          <motion.p variants={fadeIn} className="text-sm text-gray-600 pt-8" data-id="element-59">
            © {new Date().getFullYear()} The White Whisperer
          </motion.p>
        </motion.div>
      </footer>
    </div>;
}